import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Trash2, Search, Users, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminEnrollmentsPage = () => {
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const [enrollments, setEnrollments] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    course_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, usersRes, coursesRes] = await Promise.all([
        fetch(`${API}/admin/enrollments`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/courses`)
      ]);
      
      if (enrollmentsRes.ok) setEnrollments(await enrollmentsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id || !formData.course_id) {
      toast.error('Please select both user and course');
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('User enrolled successfully');
        setDialogOpen(false);
        setFormData({ user_id: '', course_id: '', notes: '' });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to enroll user');
      }
    } catch (error) {
      toast.error('Error creating enrollment');
    }
  };

  const handleCancel = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to cancel this enrollment?')) return;
    
    try {
      const response = await fetch(`${API}/admin/enrollments/${enrollmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Enrollment cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel enrollment');
      }
    } catch (error) {
      toast.error('Error cancelling enrollment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEnrollments = enrollments.filter(e => 
    e.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.course?.title?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-enrollments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            {language === 'ar' ? 'إدارة التسجيلات' : 'Enrollment Management'}
          </h1>
          <p className="text-[#6B7280]">
            {language === 'ar' ? 'تسجيل المتعلمين في الدورات' : 'Enroll learners in courses'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="add-enrollment-btn">
          <Plus className="h-4 w-4 me-2" />
          {language === 'ar' ? 'تسجيل جديد' : 'New Enrollment'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">
                  {enrollments.filter(e => e.status === 'active').length}
                </p>
                <p className="text-sm text-[#6B7280]">
                  {language === 'ar' ? 'نشط' : 'Active Enrollments'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">
                  {enrollments.filter(e => e.status === 'completed').length}
                </p>
                <p className="text-sm text-[#6B7280]">
                  {language === 'ar' ? 'مكتمل' : 'Completed'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#33AADD]/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{enrollments.length}</p>
                <p className="text-sm text-[#6B7280]">
                  {language === 'ar' ? 'إجمالي التسجيلات' : 'Total Enrollments'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
        <Input
          placeholder={language === 'ar' ? 'بحث...' : 'Search by user or course...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ps-10"
        />
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === 'ar' ? 'المتعلم' : 'Learner'}</TableHead>
              <TableHead>{language === 'ar' ? 'الدورة' : 'Course'}</TableHead>
              <TableHead>{language === 'ar' ? 'التقدم' : 'Progress'}</TableHead>
              <TableHead>{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
              <TableHead>{language === 'ar' ? 'تاريخ التسجيل' : 'Enrolled Date'}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEnrollments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-[#6B7280]">
                  {language === 'ar' ? 'لا توجد تسجيلات' : 'No enrollments found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredEnrollments.map((enrollment) => (
                <TableRow key={enrollment.enrollment_id} data-testid={`enrollment-row-${enrollment.enrollment_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-[#111827]">{enrollment.user?.name || 'N/A'}</p>
                      <p className="text-sm text-[#6B7280]">{enrollment.user?.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {language === 'ar' ? enrollment.course?.title_ar : enrollment.course?.title}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#33AADD] rounded-full"
                          style={{ width: `${enrollment.progress_percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-[#6B7280]">
                        {Math.round(enrollment.progress_percent)}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(enrollment.status)}>
                      {enrollment.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#6B7280]">
                    {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {enrollment.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancel(enrollment.enrollment_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Enrollment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'تسجيل متعلم جديد' : 'Enroll New Learner'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'المتعلم' : 'Learner'} *</Label>
              <Select 
                value={formData.user_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, user_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'ar' ? 'اختر متعلم' : 'Select learner'} />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'الدورة' : 'Course'} *</Label>
              <Select 
                value={formData.course_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'ar' ? 'اختر دورة' : 'Select course'} />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.course_id} value={course.course_id}>
                      {language === 'ar' ? course.title_ar : course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'ar' ? 'ملاحظات اختيارية...' : 'Optional notes...'}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('admin.cancel')}
              </Button>
              <Button type="submit" className="btn-primary">
                {language === 'ar' ? 'تسجيل' : 'Enroll'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEnrollmentsPage;
